<!-- 
Dashboard widget showing a bar chart of provider levy amounts.

One bar is shown for each levy collection provider in the levy collection. 
Bars run from left to right from smallest levy to biggest levy.
Widget has two modes: one has the bars showing the levy amount, the other has the bars
showing the proportion of the levy in each of the levy components (admin, base risk, risk rated, special tuition protection).
-->
<template>
  <v-flex xs12 lg8 xl6>
    <v-card flat class="home">
      <our-widget-toolbar 
        :title="title" 
        :fixed="fixed"
        @refresh="forceReload" 
        @remove="$emit('removeWidget')" 
        @toggleChartType="$emit('toggle')"
      />
      <our-busy-indicator :show="!loaded"/>
      <v-card-text class="pb-3">
        <bar-chart 
          v-if="!isChartEmpty(chartData)" ref="chart" :chart-data="chartData" :chart-options="chartOptions" 
          style="height: 265px"
        />
        <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import BarChart from "@/components/common/chart/BarChart.vue";
import _ from "underscore";
import * as formatting from "@/helpers/formatting.helper";
import * as dashboardAction from "@/store/dashboard/action-types";
import * as helpers from "@/store/levy-collections/helpers";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: {
    BarChart
  },
  mixins: [chartMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    toggled: {
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: this.chartClick,
        tooltips: {
          callbacks: {
            label: this.getTooltipLabel,
            afterBody: () => ["", "(click to show)"]
          }
        },
        scales: {
          xAxes: [{ display: false, stacked: true }],
          yAxes: [
            {
              stacked: true,
              ticks: { max: this.toggled ? 100 : undefined, callback: this.formatYAxisTicks }
            }
          ]
        }
      };
    },
    title() {
      return this.toggled ? "Levy components breakdown per provider" : "Total levy amounts per provider";
    },
    state() {
      return this.$store.state.dashboard.providerLevyAmounts;
    },
    loading() {
      return this.state.loading;
    },
    loaded() {
      return this.state.loaded;
    },
    chartData() {
      return this.toggled ? this.chartDataStacked : this.chartDataUnstacked;
    },
    chartDataStacked() {
      if (!this.loaded) return null;
      const getPercentage = (amount, total) => formatting.numericInput((amount / total) * 100, 1);
      const datasets = [
        {
          label: "Administrative fee",
          data: _(this.state.data).map(p => getPercentage(p.administrativeFeeComponent, p.totalLevy)),
          backgroundColor: helpers.ComponentChartColors[0]
        },
        {
          label: "Risk rated premium component (base)",
          data: _(this.state.data).map(p => getPercentage(p.riskRatedPremiumComponentBase, p.totalLevy)),
          backgroundColor: helpers.ComponentChartColors[1]
        },
        {
          label: "Risk rated premium component (scored)",
          data: _(this.state.data).map(p => getPercentage(p.riskRatedPremiumComponentScored, p.totalLevy)),
          backgroundColor: helpers.ComponentChartColors[2]
        },
        {
          label: "Special tuition protection",
          data: _(this.state.data).map(p => getPercentage(p.specialTuitionProtectionComponent, p.totalLevy)),
          backgroundColor: helpers.ComponentChartColors[3]
        }
      ];
      const labels = _(this.state.data).pluck("providerName");
      return { labels, datasets };
    },
    chartDataUnstacked() {
      if (!this.loaded) return null;
      const data = _(this.state.data).pluck("totalLevy");
      const labels = _(this.state.data).pluck("providerName");
      const datasets = [{ data, backgroundColor: helpers.ComponentChartColors[0] }];
      return { labels, datasets };
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    },
    toggled() {
      this.drawChart();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    forceReload() {
      this.$store.dispatch(dashboardAction.PROVIDER_LEVY_AMOUNTS_GET, this.levyCollectionId);
    },
    refresh() {
      this.$store.dispatch(dashboardAction.PROVIDER_LEVY_AMOUNTS_REFRESH, this.levyCollectionId);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart() {
      this.$nextTick(() => {
        const c = this.$refs.chart;
        if (c) {
          c.display();
          const chartObject = c.$data._chart;
          chartObject.canvas.style.cursor = "pointer";
        }
      });
    },
    removeWidget() {
      this.$emit("removeWidget");
    },
    chartClick(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const index = activePoints[0]["_index"];
        this.$router.push(
          `/levy-collections/${this.levyCollectionId}/providers/${this.state.data[index].providerId}/calculation`
        );
      }
    },
    getTooltipLabel(tooltipItem, data) {
      const index = tooltipItem.index;
      const number = this.toggled
        ? data.datasets[tooltipItem.datasetIndex].data[index] + "%"
        : formatting.currency(data.datasets[tooltipItem.datasetIndex].data[index]);
      return this.toggled ? `${number} - ${data.datasets[tooltipItem.datasetIndex].label}` : number;
    },
    formatYAxisTicks(label) {
      return this.toggled ? label + "%" : formatting.currency0(label);
    }
  }
};
</script>
